<script>
import ExploreLayout from "@/Layouts/ExploreLayout.vue";

export default {
    layout: ExploreLayout,
};
</script>

<script setup>
import { computed, ref } from "vue";
import { Head, router, usePage } from "@inertiajs/vue3";
import ExpertCard from "@/Components/Brand/sections/ExpertCard.vue";
import ExploreCategoryBand from "@/Components/Explore/ExploreCategoryBand.vue";
import ExploreHero from "@/Components/Explore/ExploreHero.vue";
import FeaturedListCard from "@/Components/Explore/FeaturedListCard.vue";
import GiftGiverPromoCard from "@/Components/Explore/GiftGiverPromoCard.vue";
import SponsorBanner from "@/Components/Explore/SponsorBanner.vue";

const props = defineProps({
    featured: {
        type: Array,
        default: () => [],
    },
    expertLists: {
        type: Array,
        default: () => [],
    },
    // Category tabs. Empty while categories are parked for launch
    // (GH-1252): the grid then shows every list, untabbed.
    expertListTabs: {
        type: Array,
        default: () => [],
    },
    sponsor: {
        type: Object,
        default: null,
    },
});

const featuredAccents = ["#ffb545", "#c5e12e"];

const activeTab = ref(props.expertListTabs[0] ?? null);

const isAuthed = computed(() => !!usePage().props.auth?.user);
const isFeaturedTab = computed(
    () =>
        props.expertListTabs.length === 0 ||
        activeTab.value === props.expertListTabs[0],
);

// "Featured" (first tab) shows everything; the rest filter by the editorial
// category map (config/dashboard.php) keyed off the lowercased tab label.
const visibleLists = computed(() => {
    if (isFeaturedTab.value) {
        return props.expertLists;
    }

    const slug = activeTab.value.toLowerCase();

    return props.expertLists.filter((list) => list.category === slug);
});

// Below 1440px only the first featured list keeps the wide card; the rest
// lead the grid as regular cards there (still without a description, like
// the wide cards), and drop out of it once the pair sits side by side.
const featuredOverflow = computed(() =>
    isFeaturedTab.value ? props.featured.slice(1) : [],
);
const gridLists = computed(() => [
    ...featuredOverflow.value,
    ...visibleLists.value,
]);

// Per-card busy uuid so the chip throbs while its request runs (GH-994).
const bookmarkBusyUuid = ref(null);

function toggleBookmark(list) {
    if (!isAuthed.value) {
        router.visit(route("login"));

        return;
    }

    if (bookmarkBusyUuid.value) {
        return;
    }

    bookmarkBusyUuid.value = list.uuid;

    const request = list.is_bookmark
        ? axios.delete(route("bookmark.destroy", { giftlist: list.uuid }))
        : axios.post(route("bookmark.create", { giftlist: list.uuid }));

    request
        .then(() => {
            list.is_bookmark = !list.is_bookmark;
        })
        .catch((error) => console.error(error))
        .finally(() => {
            bookmarkBusyUuid.value = null;
        });
}
</script>

<template>
    <Head title="Gift Ideas" />

    <!-- Below lg the page card keeps its rounded foot; from lg the purple
         section ends square with a cream gap above the footer (mock: 21px). -->
    <div class="px-5 lg:pb-5">
        <div
            class="mx-auto max-w-[1472px] overflow-hidden max-lg:rounded-b-[10px]"
        >
            <ExploreHero />

            <!-- The navy band stays as the hero seam even without tabs. -->
            <ExploreCategoryBand v-model="activeTab" :tabs="expertListTabs" />

            <section
                class="bg-[#63009c] px-4 pb-14 pt-8 sm:px-9 md:pb-20 md:pt-12 xl:px-[84px]"
            >
                <!--
                    "Newly Added" feature pair: one full-width card until two
                    horizontal cards fit side by side (1440px); below that the
                    second list joins the grid as a regular card.
                -->
                <div
                    v-if="isFeaturedTab && featured.length"
                    class="grid grid-cols-1 gap-6 min-[1440px]:grid-cols-2 min-[1440px]:gap-7"
                >
                    <div
                        v-for="(list, index) in featured"
                        :key="list.uuid"
                        class="min-w-0"
                        :class="{ 'max-[1439px]:hidden': index > 0 }"
                    >
                        <FeaturedListCard
                            :list="list"
                            :accent="
                                featuredAccents[index % featuredAccents.length]
                            "
                        />
                    </div>
                </div>

                <!-- Expert list grid; guests get the account CTA in the flow -->
                <div
                    v-if="gridLists.length"
                    class="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 md:mt-12 lg:grid-cols-3 xl:grid-cols-4 xl:gap-7"
                >
                    <ExpertCard
                        v-for="list in gridLists"
                        :key="list.uuid"
                        :class="{
                            'min-[1440px]:hidden':
                                featuredOverflow.includes(list),
                        }"
                        :creator="list.creator"
                        :title="list.title"
                        :description="
                            featuredOverflow.includes(list)
                                ? null
                                : list.description
                        "
                        :image="list.image"
                        :avatar="list.avatar"
                        :href="list.url"
                        :bookmarked="list.is_bookmark"
                        :busy="bookmarkBusyUuid === list.uuid"
                        expert
                        :masthead-photo="list.masthead_photo"
                        :masthead-focus-x="list.masthead_focus_x"
                        :masthead-focus-y="list.masthead_focus_y"
                        @bookmark="toggleBookmark(list)"
                    />

                    <GiftGiverPromoCard v-if="!isAuthed" />
                </div>

                <p
                    v-else
                    class="mt-8 rounded-brand border border-dashed border-white/40 px-4 py-10 text-center font-brand text-[15px] text-white/80 md:mt-12"
                >
                    {{
                        expertListTabs.length
                            ? "No expert lists in this category yet."
                            : "No expert lists yet."
                    }}
                </p>

                <SponsorBanner
                    v-if="sponsor"
                    :sponsor="sponsor"
                    class="mx-auto mt-10 max-w-[990px] md:mt-16"
                />
            </section>
        </div>
    </div>
</template>
